import axios, { InternalAxiosRequestConfig } from 'axios';
import { supabase } from './supabase';

const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const defaultApiUrl = isLocal ? 'http://localhost:5000/api' : 'https://api.miladesign3d.com/api';

/**
 * Retorna la URL base del backend sin el prefijo `/api` (ej. `https://api.miladesign3d.com`).
 */
export function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL?.trim();
  let base = envUrl || defaultApiUrl;

  // Si por alguna razón la URL configurada viene sin protocolo o con punto inicial (ej. '.miladesign3d.com/api')
  if (base.startsWith('.')) {
    base = base.replace(/^\.+/, '');
  }
  if (!base.startsWith('http://') && !base.startsWith('https://')) {
    base = `https://${base}`;
  }

  // Quitar '/api' o trailing slashes del final
  return base.replace(/\/api\/?$/, '').replace(/\/+$/, '');
}

/**
 * Sanitiza y resuelve la URL completa de una imagen subida o guardada.
 * Si ya tiene un prefijo roto como 'miladesign3d.com/api/uploads/...' o es relativa '/uploads/...',
 * la normaliza apuntando siempre al host de la API correcta.
 */
export function resolveImageUrl(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  const apiHost = getApiBaseUrl();

  // Si contiene '/uploads/' en cualquier parte (incluso con dominios rotos o mal formados)
  const uploadsIndex = trimmed.indexOf('/uploads/');
  if (uploadsIndex !== -1) {
    const relativePath = trimmed.substring(uploadsIndex); // '/uploads/2026/10/...'
    return `${apiHost}${relativePath}`;
  }

  // Si empieza con '/api/uploads/'
  const apiUploadsIndex = trimmed.indexOf('/api/uploads/');
  if (apiUploadsIndex !== -1) {
    const relativePath = trimmed.substring(apiUploadsIndex + 4); // '/uploads/2026/10/...'
    return `${apiHost}${relativePath}`;
  }

  // Si es una URL http/https normal (ej. imagen externa de Supabase o Unsplash)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Si es una ruta relativa local interna
  if (trimmed.startsWith('/')) {
    return `${apiHost}${trimmed}`;
  }

  return trimmed;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || defaultApiUrl,
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Protección: si el servidor web devolvió index.html por error de ruta SPA
    if (typeof response.data === 'string' && (response.data.includes('<!DOCTYPE html>') || response.data.includes('<html'))) {
      return Promise.reject(new Error('Respuesta inválida del servidor: se recibió HTML en vez de datos JSON.'));
    }
    return response;
  },
  (error: any) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

